import { z } from 'zod'

/**
 * AI 중계 메시지 (AI 중계 샘플 2부, docs/design-ai-brokered-samples.md 8절). 고객과 공장 사이에 직접 채널은 없다.
 * 한쪽이 보낸 글은 레시피캣으로 들어와 직거래 유도 정보를 걸러 낸 뒤 AI 가 받는 쪽에 맞게 다시 쓰고(튜닝), 원안 요약과
 * 달라진 점을 붙여 전달한다. 원문·걸러 낸 글·AI 변환본은 모두 감사 기록으로 남는다.
 */

export const SAMPLE_MESSAGE_DIRECTIONS = ['factory_to_customer', 'customer_to_factory'] as const
export type SampleMessageDirection = (typeof SAMPLE_MESSAGE_DIRECTIONS)[number]

/** message = 일반 메시지, counter_offer = 공장의 역제안(배합·공정·가격·일정을 바꾸자는 제안) */
export const SAMPLE_MESSAGE_KINDS = ['message', 'counter_offer'] as const
export type SampleMessageKind = (typeof SAMPLE_MESSAGE_KINDS)[number]

/**
 * processing = AI 가 다시 쓰는 중 · awaiting_customer = 공장 글이 고객 앱의 AI 동의 표시를 기다림(고객이 대화를 열면 처리) ·
 * delivered = 받는 쪽에 보임 · review = 관리자 대기열(AI 실패·효능 표현·AI 동의 없음·내용 없음) · blocked = 관리자가 막음
 */
export const SAMPLE_MESSAGE_STATUSES = [
  'processing',
  'awaiting_customer',
  'delivered',
  'review',
  'blocked',
] as const
export type SampleMessageStatus = (typeof SAMPLE_MESSAGE_STATUSES)[number]

/** 관리자 대기열로 간 까닭 */
export const SAMPLE_MESSAGE_REVIEW_REASONS = [
  'ai_failed',
  'health_claim',
  'no_ai_consent',
  'contact_only',
] as const
export type SampleMessageReviewReason = (typeof SAMPLE_MESSAGE_REVIEW_REASONS)[number]

/** 고객·공장이 보내는 글 */
export const sendSampleMessageSchema = z.object({
  kind: z.enum(SAMPLE_MESSAGE_KINDS).default('message'),
  text: z.string().trim().min(1).max(2000),
})
export type SendSampleMessageInput = z.infer<typeof sendSampleMessageSchema>

/** 고객 앱의 대화 요청에 붙는 AI 전송 동의 표시 (앱 ai-consent.ts aiConsentQuery 와 같은 칸) */
export const sampleMessagesQuerySchema = z.object({ aiConsent: z.enum(['0', '1']).optional() })

/** 관리자 대기열 처리: 고쳐 보내기 또는 막기 */
export const adminSampleMessageActionSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('send'), text: z.string().trim().min(1).max(4000) }),
  z.object({ action: z.literal('block'), note: z.string().trim().min(1).max(500) }),
])
export type AdminSampleMessageAction = z.infer<typeof adminSampleMessageActionSchema>

/** AI 가 돌려주는 모양 (받는 쪽에 보이는 세 덩어리) */
export const sampleRelayOutputSchema = z.object({
  /** 받는 쪽에 맞게 다시 쓴 글 — 고객에게는 조건에 맞춘 제안, 공장에는 바로 반영할 요청 */
  tuned: z.string().trim().min(1).max(2000),
  /** 보낸 쪽 원안 요약 */
  summary: z.string().trim().min(1).max(600),
  /** 원안과 달라진 점·고객 조건과 부딪히는 점 */
  changes: z.array(z.string().trim().min(1).max(200)).max(8),
})
export type SampleRelayOutput = z.infer<typeof sampleRelayOutputSchema>

/** 대화 한 줄. 받는 쪽에는 전달된 것만, 보낸 쪽에는 자기 글(걸러 낸 글)과 상태가 보인다 */
export interface SampleMessageView {
  id: string
  direction: SampleMessageDirection
  kind: SampleMessageKind
  status: SampleMessageStatus
  /** 이 화면의 주인이 보낸 글인지 */
  mine: boolean
  /** 보낸 쪽에만: 걸러 낸 뒤의 내 글 */
  sentText: string | null
  /** 받는 쪽에만, 전달된 뒤: AI 가 다시 쓴 글·원안 요약·달라진 점 */
  relayed: SampleRelayOutput | null
  createdAt: string
}

/** 관리자 대기열·감사 1행 — 원문까지 */
export interface AdminSampleMessageView extends SampleMessageView {
  orderId: string
  senderRole: 'customer' | 'factory' | 'admin'
  original: string
  filtered: string
  filterHits: string[]
  reviewReason: SampleMessageReviewReason | null
}
